import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import config from '../config';
import { useRouter } from 'next/router';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { enableContractorSignup } = config.features;

  // Determine contractor auth state via session API (cookie is HttpOnly and not accessible via document.cookie)
  const [isContractorLoggedIn, setIsContractorLoggedIn] = useState(false);

  const router = useRouter();

  // Centralised function so it can be reused on route changes
  const checkSession = async () => {
    try {
      const res = await fetch('/api/contractor/auth/session');
      const data = await res.json();
      setIsContractorLoggedIn(data.authenticated);
    } catch (err) {
      console.error('Failed to check contractor session', err);
      setIsContractorLoggedIn(false);
    }
  };

  // Initial check on mount
  useEffect(() => {
    checkSession();
  }, []);

  // Re-check whenever the route changes so UI updates right after login/logout
  useEffect(() => {
    router.events.on('routeChangeComplete', checkSession);
    return () => {
      router.events.off('routeChangeComplete', checkSession);
    };
  }, [router.events]);

  const handleSignOut = async () => {
    try {
      await fetch('/api/contractor/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('Logout failed', err);
    } finally {
      // Redirect regardless of API success to ensure session cookie cleared client-side
      window.location.href = '/';
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="bg-white shadow-md">
      <div className="container mx-auto px-4 flex justify-between items-center py-5">
        <Link href="/" className="flex items-center">
          <div className="flex items-center justify-center" style={{ minWidth: '240px' }}>
            <Image 
              src="/images/logo.png" 
              alt="Water Damage Direct" 
              className="h-24 w-auto"
              width={240}
              height={96}
              fill={false}
              priority
            />
          </div>
        </Link>
        
        <nav className="hidden md:flex space-x-6 text-[#004C8C]">
          <Link href="/search" className="hover:text-blue-700 transition font-medium flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#004C8C]" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            Find Near Me
          </Link>
          {/* Only show Find by State if doorway pages are enabled */}
          {config.features.enableDoorwayPages && (
            <Link href="/states" className="hover:text-blue-700 transition font-medium flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#004C8C]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              Find by State
            </Link>
          )}
          <Link href="/emergency" className="hover:text-blue-700 transition font-medium">
            Emergency Guide
          </Link>
          {process.env.NEXT_PUBLIC_ENABLE_QUIZ === 'true' && (
            <Link href="/water-damage-action-plan" className="hover:text-blue-700 transition font-medium">
              Water Damage Action Plan
            </Link>
          )}
          <Link href="/faq" className="hover:text-blue-700 transition font-medium">
            FAQ
          </Link>
        </nav>
        
        <div className="flex items-center space-x-4">
          {enableContractorSignup && !isContractorLoggedIn && (
            <div className="hidden md:flex space-x-2">
              <Link 
                href="/contractor" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition duration-300 font-semibold"
                prefetch={false}
              >
                For Contractors
              </Link>
              <Link 
                href="/contractor/login" 
                className="bg-white hover:bg-gray-100 text-blue-600 border border-blue-600 px-4 py-2 rounded-md transition duration-300 font-semibold"
                prefetch={false}
              >
                Login
              </Link>
            </div>
          )}
          {isContractorLoggedIn && (
            <button 
              onClick={handleSignOut}
              className="hidden md:inline-block bg-white hover:bg-gray-100 text-red-600 border border-red-600 px-4 py-2 rounded-md transition duration-300 font-semibold"
            >
              Sign Out
            </button>
          )}
          <button 
            className="md:hidden text-[#004C8C] clickable"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 px-4 py-3">
          <nav className="flex flex-col space-y-3 text-[#004C8C]">
            <Link 
              href="/search"
              className="hover:text-blue-700 transition font-medium py-2 flex items-center gap-2"
              onClick={toggleMobileMenu}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#004C8C]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Find Near Me
            </Link>
            {/* Only show Find by State if doorway pages are enabled */}
            {config.features.enableDoorwayPages && (
              <Link 
                href="/states" 
                className="hover:text-blue-700 transition font-medium py-2 flex items-center gap-2"
                onClick={toggleMobileMenu}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#004C8C]" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
                Find by State
              </Link>
            )}
            <Link 
              href="/emergency" 
              className="hover:text-blue-700 transition font-medium py-2"
              onClick={toggleMobileMenu}
            >
              Emergency Guide
            </Link>
            {process.env.NEXT_PUBLIC_ENABLE_QUIZ === 'true' && (
              <Link 
                href="/water-damage-action-plan" 
                className="hover:text-blue-700 transition font-medium py-2"
                onClick={toggleMobileMenu}
              >
                Water Damage Action Plan
              </Link>
            )}
            <Link 
              href="/faq" 
              className="hover:text-blue-700 transition font-medium py-2"
              onClick={toggleMobileMenu}
            >
              FAQ
            </Link>
            {enableContractorSignup && (
              <>
                <Link 
                  href="/contractor" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition duration-300 inline-block"
                  onClick={toggleMobileMenu}
                >
                  For Contractors
                </Link>
                <Link 
                  href="/contractor/login" 
                  className="bg-white hover:bg-gray-100 text-blue-600 border border-blue-600 px-4 py-2 rounded-md transition duration-300 inline-block mt-2"
                  onClick={toggleMobileMenu}
                >
                  Contractor Login
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}